// /settings/devices — управление сессиями. Тактильный «Картотека».
// См. docs/ui-spec/settings.devices.{desktop,mobile}.md.

import { Badge, Button, CategoryTile, EmptyState, ErrorState } from "@studyhub/ui";
import { SwipeActions } from "@studyhub/ui/components/swipe-actions";
import { toast } from "@studyhub/ui/toast";
import {
	IconBell,
	IconDeviceDesktop,
	IconDeviceMobile,
	IconDevices,
	IconStarFilled,
	IconTrash,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsSection } from "@/components/settings/settings-shell";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useConfirm } from "@/hooks/use-confirm";
import { useRefetchOnOnline } from "@/hooks/use-refetch-on-online";
import { cn } from "@/lib/cn";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";

export const Route = createFileRoute("/settings/devices")({
	component: DevicesRoute,
	pendingComponent: PagePendingFallback,
	validateSearch: (s: Record<string, unknown>): { demo?: string } => ({
		demo: typeof s.demo === "string" ? s.demo : undefined,
	}),
});

interface Device {
	id: string;
	name: string;
	platform: "web" | "android" | "ios";
	os: string;
	location: string;
	lastUsed: string;
	isCurrent: boolean;
	pushChannel?: "web" | "fcm" | "apns" | null;
}

interface DeviceSessionRow {
	id: string;
	platform: "web" | "android" | "ios";
	deviceName: string;
	geoCity: string | null;
	geoCountry: string | null;
	ipAddress: string | null;
	lastActivityAt: string;
	createdAt: string;
	isCurrent: boolean;
}

function relativeTime(iso: string, locale: string): string {
	const diffMs = Date.now() - new Date(iso).getTime();
	const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
	const min = Math.round(diffMs / 60_000);
	if (Math.abs(min) < 60) return rtf.format(-min, "minute");
	const hours = Math.round(min / 60);
	if (Math.abs(hours) < 24) return rtf.format(-hours, "hour");
	return rtf.format(-Math.round(hours / 24), "day");
}

function mapDevice(row: DeviceSessionRow, locale: string): Device {
	const loc = [row.geoCity, row.geoCountry].filter(Boolean).join(", ");
	const pushChannel: Device["pushChannel"] =
		row.platform === "web" ? "web" : row.platform === "android" ? "fcm" : "apns";
	return {
		id: row.id,
		name: row.deviceName,
		platform: row.platform,
		os: row.platform,
		location: loc || (row.ipAddress ?? ""),
		lastUsed: relativeTime(row.lastActivityAt, locale),
		isCurrent: row.isCurrent,
		pushChannel,
	};
}

function DevicesRoute() {
	const { t, i18n } = useTranslation("settings");
	const search = Route.useSearch();
	const utils = api.useUtils();
	const confirm = useConfirm();
	const query = api.auth.devices.list.useQuery(undefined, { staleTime: 30_000 });
	useRefetchOnOnline(() => query.refetch(), query.isError);
	const revokeMutation = api.auth.devices.revoke.useMutation({
		onSuccess: () => {
			void utils.auth.devices.list.invalidate();
			toast.success(t("devices.revokeSuccess"));
		},
	});
	const revokeAllMutation = api.auth.devices.revokeAll.useMutation({
		onSuccess: () => {
			void utils.auth.devices.list.invalidate();
			toast.success(t("devices.revokeAllSuccess"));
		},
	});
	const devices = useMemo<Device[]>(() => {
		const rows = (query.data as DeviceSessionRow[] | undefined) ?? [];
		return rows.map((r) => mapDevice(r, i18n.language));
	}, [query.data, i18n.language]);

	const loading = search.demo === "loading" || query.isLoading;
	const error = search.demo === "error" || query.isError;

	async function onRevoke(d: Device): Promise<void> {
		const ok = await confirm({
			title: t("devices.revokeConfirmTitle"),
			description: t("devices.revokeConfirmBody", { device: d.name }),
			confirmLabel: t("devices.revoke"),
			tone: "destructive",
		});
		if (!ok) return;
		revokeMutation.mutate({ id: d.id });
	}

	async function onRevokeAll(): Promise<void> {
		const ok = await confirm({
			title: t("devices.revokeAllConfirmTitle"),
			description: t("devices.revokeAllConfirmBody"),
			confirmLabel: t("devices.revokeAll"),
			tone: "destructive",
		});
		if (!ok) return;
		revokeAllMutation.mutate();
	}

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("devices.title")}
				description={t("devices.subtitle")}
				icon={<IconDevices stroke={1.75} />}
				actions={
					devices.length > 1 ? (
						<Button variant="destructive" size="sm" onClick={() => void onRevokeAll()}>
							{t("devices.revokeAll")}
						</Button>
					) : undefined
				}
			/>

			{error ? (
				<ErrorState
					title={t("devices.loadErrorTitle")}
					description={t("devices.loadErrorBody")}
					action={
						<Button
							size="sm"
							variant="outline"
							onClick={() => void utils.auth.devices.list.invalidate()}
						>
							{t("devices.retry")}
						</Button>
					}
				/>
			) : loading ? (
				<SettingsSection>
					{Array.from({ length: 3 }).map((_, i) => (
						<DeviceSkeleton key={i} />
					))}
				</SettingsSection>
			) : devices.length === 0 ? (
				<EmptyState
					icon={<IconDevices />}
					title={t("devices.emptyTitle")}
					description={t("devices.emptyBody")}
				/>
			) : (
				<>
					<SettingsSection className="stagger-enter">
						{devices.map((d) => (
							<DeviceRow key={d.id} device={d} onRevoke={() => void onRevoke(d)} />
						))}
					</SettingsSection>

					<p className="mt-2 px-4 text-xs text-muted-foreground lg:px-0">
						{t("devices.notRecognizedHint")}
					</p>
				</>
			)}
		</SettingsPageContainer>
	);
}

function DeviceRow({ device, onRevoke }: { device: Device; onRevoke: () => void }) {
	const { t } = useTranslation("settings");
	const Icon = device.platform === "web" ? IconDeviceDesktop : IconDeviceMobile;

	const row = (
		<div
			className={cn(
				"tap-press flex items-start gap-3 px-4 py-4 transition-colors duration-fast",
				device.isCurrent ? "bg-primary-soft/60" : "hover:bg-[var(--surface-2)]",
			)}
		>
			<CategoryTile
				category={device.isCurrent ? "ai" : "doc"}
				icon={<Icon stroke={1.75} />}
				size="lg"
				className="mt-0.5"
			/>
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-sm font-medium text-foreground">{device.name}</span>
					{device.isCurrent && (
						<Badge variant="outline" className="gap-1 border-primary/30 text-2xs text-primary">
							<IconStarFilled size={9} />
							{t("devices.current")}
						</Badge>
					)}
				</div>
				<div className="mt-0.5 truncate text-xs text-muted-foreground">
					{device.location} · {device.os}
				</div>
				<div className="mt-0.5 text-xs tabular-nums text-muted-foreground">
					{t("devices.lastActive", { when: device.lastUsed })}
				</div>
				{device.pushChannel ? (
					<div className="mt-1.5 flex items-center gap-1 text-xs text-success">
						<IconBell size={12} stroke={1.75} />
						{t("devices.pushActive")}
					</div>
				) : (
					<div className="mt-1.5 text-xs text-muted-foreground">
						{t("devices.pushNotConfigured")}
					</div>
				)}
			</div>
			{/* Desktop-фолбэк: кнопка «Отозвать». Показываем ровно когда НЕ активен
			    свайп (mobile-платформа), мирроря условие SwipeActions ниже — иначе на
			    планшете 640–1023 (свайп off, viewport-класс прятал бы кнопку) отзыв
			    был бы недоступен. */}
			{!device.isCurrent && !IS_MOBILE_PLATFORM && (
				<Button variant="outline" size="sm" className="shrink-0" onClick={onRevoke}>
					{t("devices.revoke")}
				</Button>
			)}
		</div>
	);

	// Mobile: свайп влево по строке → отозвать сессию (destructive).
	// Текущее устройство отозвать нельзя — без свайпа.
	if (IS_MOBILE_PLATFORM && !device.isCurrent) {
		return (
			<SwipeActions
				rightActions={[
					{
						key: "revoke",
						label: t("devices.revoke"),
						icon: <IconTrash />,
						tone: "destructive",
						onAction: onRevoke,
					},
				]}
			>
				{row}
			</SwipeActions>
		);
	}

	return row;
}

function DeviceSkeleton() {
	return (
		<div className="flex items-start gap-3 px-4 py-4">
			<div className="size-11 shrink-0 animate-pulse rounded-[11px] bg-muted" />
			<div className="flex-1 space-y-2">
				<div className="h-4 w-48 animate-pulse rounded bg-muted" />
				<div className="h-3 w-32 animate-pulse rounded bg-muted" />
			</div>
		</div>
	);
}
